import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von09Ophthalmology } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von09OphthalmologyFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von09OphthalmologyForm: React.FC<Von09OphthalmologyFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [ropExam, setRopExam] = useState(false);
  const [ropDateDiag, setRopDateDiag] = useState<string>('');
  const [ropEye, setRopEye] = useState<string>('');
  const [ropZone, setRopZone] = useState<string>('');
  const [ropStage, setRopStage] = useState<string>('');
  const [ropPlus, setRopPlus] = useState(false);
  const [ropAntiVegf, setRopAntiVegf] = useState(false);
  const [ropAntiVegfAgent, setRopAntiVegfAgent] = useState<string>('');
  const [ropAntiVegfDate, setRopAntiVegfDate] = useState<string>('');
  const [ropAntiVegfWhere, setRopAntiVegfWhere] = useState<string>('');
  const [ropLaser, setRopLaser] = useState(false);
  const [ropLaserDate, setRopLaserDate] = useState<string>('');
  const [ropLaserWhere, setRopLaserWhere] = useState<string>('');
  const [ropSurgery, setRopSurgery] = useState(false);
  const [ropSurgeryDate, setRopSurgeryDate] = useState<string>('');
  const [ropSurgeryType, setRopSurgeryType] = useState<string>('');
  const [ropSurgeryWhere, setRopSurgeryWhere] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getOphthalmology(patientId);

      if (data) {
        setHasExistingData(true);
        setRopExam(Boolean(data.rop_exam));
        setRopDateDiag(data.rop_date_diag || '');
        setRopEye(data.rop_eye || '');
        setRopZone(data.rop_zone?.toString() || '');
        setRopStage(data.rop_stage?.toString() || '');
        setRopPlus(Boolean(data.rop_plus));
        setRopAntiVegf(Boolean(data.rop_anti_vegf));
        setRopAntiVegfAgent(data.rop_anti_vegf_agent?.toString() || '');
        setRopAntiVegfDate(data.rop_anti_vegf_date || '');
        setRopAntiVegfWhere(data.rop_anti_vegf_where?.toString() || '');
        setRopLaser(Boolean(data.rop_laser));
        setRopLaserDate(data.rop_laser_date || '');
        setRopLaserWhere(data.rop_laser_where?.toString() || '');
        setRopSurgery(Boolean(data.rop_surgery));
        setRopSurgeryDate(data.rop_surgery_date || '');
        setRopSurgeryType(data.rop_surgery_type?.toString() || '');
        setRopSurgeryWhere(data.rop_surgery_where?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von09Ophthalmology> = {
        patient_id: patientId,
        rop_exam: ropExam,
        rop_date_diag: ropDateDiag || undefined,
        rop_eye: ropEye || undefined,
        rop_zone: ropZone ? parseInt(ropZone) : undefined,
        rop_stage: ropStage ? parseInt(ropStage) : undefined,
        rop_plus: ropPlus,
        rop_anti_vegf: ropAntiVegf,
        rop_anti_vegf_agent: ropAntiVegfAgent ? parseInt(ropAntiVegfAgent) : undefined,
        rop_anti_vegf_date: ropAntiVegfDate || undefined,
        rop_anti_vegf_where: ropAntiVegfWhere ? parseInt(ropAntiVegfWhere) : undefined,
        rop_laser: ropLaser,
        rop_laser_date: ropLaserDate || undefined,
        rop_laser_where: ropLaserWhere ? parseInt(ropLaserWhere) : undefined,
        rop_surgery: ropSurgery,
        rop_surgery_date: ropSurgeryDate || undefined,
        rop_surgery_type: ropSurgeryType ? parseInt(ropSurgeryType) : undefined,
        rop_surgery_where: ropSurgeryWhere ? parseInt(ropSurgeryWhere) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateOphthalmology(patientId, formData);
      } else {
        await vonDataService.createOphthalmology(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 09: Oftalmologie - ROP</CardTitle>
        <CardDescription>Date despre retinopatie de prematuritate</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <h3 className="text-sm font-medium">Examinare și tratamente</h3>

          {/* ROP examination */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ropExam"
                  checked={ropExam}
                  onCheckedChange={(checked) => setRopExam(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ropExam" className="text-sm font-normal cursor-pointer">
                  Examen ROP efectuat
                </Label>
              </div>

              {ropExam && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ropDateDiag">Data diagnostic ROP</Label>
                    <Input
                      id="ropDateDiag"
                      type="date"
                      value={ropDateDiag}
                      onChange={(e) => setRopDateDiag(e.target.value)}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropEye">Ochi afectat</Label>
                    <Select value={ropEye} onValueChange={setRopEye} disabled={readOnly}>
                      <SelectTrigger id="ropEye">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Stâng</SelectItem>
                        <SelectItem value="2">Drept</SelectItem>
                        <SelectItem value="3">Bilateral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropZone">Zonă ROP</Label>
                    <Select value={ropZone} onValueChange={setRopZone} disabled={readOnly}>
                      <SelectTrigger id="ropZone">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Zona I</SelectItem>
                        <SelectItem value="2">Zona II</SelectItem>
                        <SelectItem value="3">Zona III</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropStage">Stadiul ROP (cel mai grav)</Label>
                    <Select value={ropStage} onValueChange={setRopStage} disabled={readOnly}>
                      <SelectTrigger id="ropStage">
                        <SelectValue placeholder="Selectează stadiul..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Stadiul 0</SelectItem>
                        <SelectItem value="1">Stadiul 1</SelectItem>
                        <SelectItem value="2">Stadiul 2</SelectItem>
                        <SelectItem value="3">Stadiul 3</SelectItem>
                        <SelectItem value="4">Stadiul 4</SelectItem>
                        <SelectItem value="5">Stadiul 5</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="ropPlus"
                      checked={ropPlus}
                      onCheckedChange={(checked) => setRopPlus(checked === true)}
                      disabled={readOnly}
                    />
                    <Label htmlFor="ropPlus" className="text-sm font-normal cursor-pointer">
                      Boală plus
                    </Label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Anti-VEGF treatment */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ropAntiVegf"
                  checked={ropAntiVegf}
                  onCheckedChange={(checked) => setRopAntiVegf(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ropAntiVegf" className="text-sm font-normal cursor-pointer">
                  Tratament anti-VEGF administrat
                </Label>
              </div>

              {ropAntiVegf && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ropAntiVegfAgent">Agent anti-VEGF</Label>
                    <Select value={ropAntiVegfAgent} onValueChange={setRopAntiVegfAgent} disabled={readOnly}>
                      <SelectTrigger id="ropAntiVegfAgent">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Bevacizumab</SelectItem>
                        <SelectItem value="2">Ranibizumab</SelectItem>
                        <SelectItem value="3">Aflibercept</SelectItem>
                        <SelectItem value="99">Altul</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropAntiVegfDate">Data administrare anti-VEGF</Label>
                    <Input
                      id="ropAntiVegfDate"
                      type="date"
                      value={ropAntiVegfDate}
                      onChange={(e) => setRopAntiVegfDate(e.target.value)}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropAntiVegfWhere">Unde a fost administrat</Label>
                    <Select value={ropAntiVegfWhere} onValueChange={setRopAntiVegfWhere} disabled={readOnly}>
                      <SelectTrigger id="ropAntiVegfWhere">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                        <SelectItem value="2">Alt spital</SelectItem>
                        <SelectItem value="3">Ambele</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Laser treatment */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ropLaser"
                  checked={ropLaser}
                  onCheckedChange={(checked) => setRopLaser(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ropLaser" className="text-sm font-normal cursor-pointer">
                  Tratament laser ROP
                </Label>
              </div>

              {ropLaser && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ropLaserDate">Data tratament laser</Label>
                    <Input
                      id="ropLaserDate"
                      type="date"
                      value={ropLaserDate}
                      onChange={(e) => setRopLaserDate(e.target.value)}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropLaserWhere">Unde a fost efectuat</Label>
                    <Select value={ropLaserWhere} onValueChange={setRopLaserWhere} disabled={readOnly}>
                      <SelectTrigger id="ropLaserWhere">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                        <SelectItem value="2">Alt spital</SelectItem>
                        <SelectItem value="3">Ambele</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Surgery */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ropSurgery"
                  checked={ropSurgery}
                  onCheckedChange={(checked) => setRopSurgery(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ropSurgery" className="text-sm font-normal cursor-pointer">
                  Chirurgie ROP
                </Label>
              </div>

              {ropSurgery && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ropSurgeryDate">Data chirurgiei</Label>
                    <Input
                      id="ropSurgeryDate"
                      type="date"
                      value={ropSurgeryDate}
                      onChange={(e) => setRopSurgeryDate(e.target.value)}
                      disabled={readOnly}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropSurgeryType">Tip chirurgie</Label>
                    <Select value={ropSurgeryType} onValueChange={setRopSurgeryType} disabled={readOnly}>
                      <SelectTrigger id="ropSurgeryType">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Vitrectomie cu păstrarea cristalinului</SelectItem>
                        <SelectItem value="2">Pliaj scleral</SelectItem>
                        <SelectItem value="99">Altele</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ropSurgeryWhere">Unde a fost efectuată</Label>
                    <Select value={ropSurgeryWhere} onValueChange={setRopSurgeryWhere} disabled={readOnly}>
                      <SelectTrigger id="ropSurgeryWhere">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                        <SelectItem value="2">Alt spital</SelectItem>
                        <SelectItem value="3">Ambele</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
